import React, { memo } from 'react';
import { Accordion as MuiAccordion, AccordionSummary, AccordionDetails } from '@mui/material';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/ArrowDown.svg';

import { useStyles } from './styles';

export interface IAccordionProps {
  /**
   * If true, expands the accordion (controlled mode)
   */
  expanded?: boolean;
  /**
   * If true, the accordion is initially expanded (uncontrolled mode)
   */
  defaultExpanded?: boolean;
  /**
   * The summary content (header)
   */
  summary: React.ReactNode;
  /**
   * The details content (body)
   */
  children: React.ReactNode;
  /**
   * Optional className for root
   */
  accordionStyleClass?: string;
  /**
   * Optional props for AccordionSummary
   */
  summaryProps?: React.ComponentProps<typeof AccordionSummary>;
  /**
   * Optional props for AccordionDetails
   */
  detailsProps?: React.ComponentProps<typeof AccordionDetails>;
  /**
   * Callback fired when the accordion is expanded/collapsed
   */
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
  /**
   * Sets data-testid on the root accordion element
   */
  testId?: string;
}

const Accordion = ({
  expanded,
  defaultExpanded = false,
  summary,
  children,
  accordionStyleClass,
  summaryProps,
  detailsProps,
  onChange,
  testId,
}: IAccordionProps) => {
  const classes = useStyles();
  const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);

  // Determine if controlled
  const isControlled = typeof expanded === 'boolean';
  const currentExpanded = isControlled ? expanded : internalExpanded;

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (!isControlled) {
      setInternalExpanded(isExpanded);
    }
    onChange?.(event, isExpanded);
  };

  return (
    <MuiAccordion
      elevation={0}
      expanded={currentExpanded}
      onChange={handleChange}
      className={`${classes.accordion} ${accordionStyleClass || ''}`}
      data-testid={testId}
    >
      <AccordionSummary
        expandIcon={<ArrowDownIcon className={classes.arrowIcon} />}
        {...summaryProps}
        className={`${classes.accordionSummary} ${summaryProps?.className || ''}`}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails {...detailsProps} className={`${classes.accordionDetails} ${detailsProps?.className || ''}`}>
        {children}
      </AccordionDetails>
    </MuiAccordion>
  );
};

export default memo(Accordion);
